import React, { Component, ErrorInfo, ReactElement, ReactNode } from "react";

import { datadogRum } from "@datadog/browser-rum";

interface ErrorBoundaryProps {
  children: ReactNode;
  FallbackComponent: ReactElement;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so next render shows fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error to Datadog RUM
    datadogRum.addError(error, {
      errorInfo,
      context: {
        triggeredBy: "ErrorBoundary"
      }
    });

    // Track error boundary crashes as a metric
    datadogRum.addAction("error_boundary_crash", {
      errorName: error.name,
      errorMessage: error.message,
      componentStack: errorInfo.componentStack
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // Use the provided fallback component
      return React.cloneElement(this.props.FallbackComponent, {
        error: this.state.error
      });
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
